import { useContext, useEffect, useState } from 'react'
import { CurrencyContext, CurrencyData } from '../../pages/_app'
import { CURRENCIES } from '../constants'
import Cookies from 'js-cookie'

type AppCurrency = (typeof CURRENCIES)[number]

/**
 * A hook that provides access to the server-side currency data
 * and handles client-side updates when needed.
 *
 * @returns {Object} Currency information with methods to update it
 */
export function useServerCurrency() {
  // Get context data from server-side props
  const contextData = useContext(CurrencyContext)

  // Client-side fallback state
  const [clientData, setClientData] = useState<CurrencyData | null>(null)

  // Initialize the client data if no context data is available
  useEffect(() => {
    if (!contextData) {
      const countryCookie = Cookies.get('country') || 'DE'
      const currencyOverrideCookie = Cookies.get('currency_override') || null

      // Populate client data
      setClientData({
        countryCode: countryCookie,
        detectedCurrency: 'EUR', // Default, will be updated in the next effect
        currencyOverride: currencyOverrideCookie,
        currentCurrency: 'EUR', // Default, will be updated in the next effect
      })
    }
  }, [contextData])

  // Update currency values whenever client data changes
  useEffect(() => {
    if (!contextData && clientData) {
      // Import helper functions dynamically to avoid server/client mismatch
      import('../helper/currencyHelper').then(({ getCurrencyFromCountry }) => {
        const detectedCurrency = getCurrencyFromCountry(clientData.countryCode)

        // Check for valid override
        const isValidOverride =
          clientData.currencyOverride &&
          clientData.currencyOverride !== 'detect' &&
          CURRENCIES.includes(clientData.currencyOverride as AppCurrency)

        // Use override if valid, otherwise use detected currency
        const currentCurrency = isValidOverride
          ? (clientData.currencyOverride as AppCurrency)
          : detectedCurrency

        // Update client data
        setClientData({
          ...clientData,
          detectedCurrency,
          currentCurrency,
        })
      })
    }
  }, [contextData, clientData?.countryCode, clientData?.currencyOverride])

  // Helper function to set currency override
  const setCurrencyOverride = (value: AppCurrency | 'detect') => {
    // Set cookie
    Cookies.set('currency_override', value, { expires: 30, path: '/' })

    // Update client data if we're not using server data
    if (!contextData && clientData) {
      setClientData({
        ...clientData,
        currencyOverride: value,
      })
    } else if (contextData) {
      // If we have server data, we need to force a page reload to get new server props
      window.location.replace(window.location.href)
    }
  }

  // Use context data if available, otherwise use client data
  const data = contextData ||
    clientData || {
      countryCode: 'DE',
      detectedCurrency: 'EUR',
      currencyOverride: null,
      currentCurrency: 'EUR',
    }

  return {
    country: data.countryCode,
    currency: data.currentCurrency,
    detectedCurrency: data.detectedCurrency,
    currencyOverride: data.currencyOverride,
    isServerData: !!contextData,
    setCurrencyOverride,
  }
}
